import {
  DefinitionActionKindEnum,
  DefinitionActionSubKindEnum,
  type ApplicationResponse,
  type CreatingApplicationCommand,
} from '@acme/api';
import { findAction } from '@acme/features';
import type { FormModel } from '@acme/foundation';
import { createApiErrorFromCaught } from '@acme/foundation';
import type { ApplicationFormActionHandlers } from '../model';
import { commandToEditDraftBody } from '../model/commandMappers';
import type { ApplicationFormationFormCtx } from '../model/formConfig';
import type { ParsedBadRequest } from './parseSubmitError';
import { parseBadRequestError } from './parseSubmitError';

export type BuildCommandFn = (opts: {
  mode: 'draft' | 'review';
  form: FormModel<ApplicationFormationFormCtx>;
  walletsForm: FormModel<Record<string, unknown>>;
  walletsCount: number;
  showIncomingWallets: boolean;
}) => Promise<Partial<CreatingApplicationCommand>>;

export type ActionPropsResult = {
  props: {
    applicationId: string | undefined;
    actions: Array<{ id?: string; kind?: number; subKind?: number }> | null;
    rework: {
      handler: () => void;
      editDraft?: { handler: () => Promise<void> };
    };
  };
};

export function buildActionProps(params: {
  application: ApplicationResponse | null;
  actionHandlers: ApplicationFormActionHandlers | null;
  buildCommand: BuildCommandFn;
  form: FormModel<ApplicationFormationFormCtx>;
  walletsForm: FormModel<Record<string, unknown>>;
  walletsCount: number;
  showIncomingWallets: boolean;
  onBadRequest?: (parsed: ParsedBadRequest) => void;
}): ActionPropsResult | null {
  const {
    application,
    actionHandlers,
    buildCommand,
    form,
    walletsForm,
    walletsCount,
    showIncomingWallets,
    onBadRequest,
  } = params;
  if (!application || !actionHandlers) return null;

  const actions = application.actions ?? null;

  const handleFormEditDraft = async () => {
    const a = findAction(
      actions,
      DefinitionActionKindEnum.Form,
      DefinitionActionSubKindEnum.FormEditDraft
    );
    if (!a?.id || !actionHandlers.onFormEditDraft) return;
    try {
      const command = await buildCommand({
        mode: 'draft',
        form,
        walletsForm,
        walletsCount,
        showIncomingWallets,
      });
      const requestBody = commandToEditDraftBody(
        command as Partial<CreatingApplicationCommand> & {
          counterpartyAddress?: string | null;
        }
      );
      await actionHandlers.onFormEditDraft(a.id, requestBody);
    } catch (err) {
      if (onBadRequest) {
        const parsed = parseBadRequestError(err);
        if (parsed) {
          onBadRequest(parsed);
          return;
        }
        const apiErr = createApiErrorFromCaught(
          err,
          'Не удалось сохранить черновик'
        );
        if (apiErr?.errorCode === 400) {
          onBadRequest({
            message: apiErr.message,
            details: apiErr.errorDetails ?? [],
          });
          return;
        }
      }
      throw err;
    }
  };

  return {
    props: {
      applicationId: application.id ?? undefined,
      actions,
      rework: {
        handler: () => actionHandlers.onRework?.(),
        editDraft: actionHandlers.onFormEditDraft
          ? { handler: handleFormEditDraft }
          : undefined,
      },
    },
  };
}
